import React from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useProfile, usePaystubs, useTransactions } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const getMonthlyNetIncome = (paystubs) => {
  // Assume bi-weekly pay, average net per paystub * 26 / 12
  if (!paystubs?.length) return 0;
  const avgNet = paystubs.reduce((sum, p) => sum + p.net, 0) / paystubs.length;
  return Math.round((avgNet * 26) / 12);
};

const calculateActualSpending = (transactions, targetMonth = null) => {
  if (!transactions?.length) return { total: 0, byCategory: {} };
  
  // If no target month specified, use current month or most recent month with data
  const currentDate = new Date();
  const defaultMonth = targetMonth || `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  
  const monthlyTransactions = transactions.filter(tx => 
    tx.date.startsWith(defaultMonth) && tx.amount < 0 // Only expenses
  );
  
  const byCategory = {};
  let total = 0;
  
  monthlyTransactions.forEach(tx => {
    const category = tx.category;
    const amount = Math.abs(tx.amount);
    
    if (!byCategory[category]) {
      byCategory[category] = 0;
    }
    byCategory[category] += amount;
    total += amount;
  });
  
  return { total: Math.round(total * 100) / 100, byCategory };
};

const getBudgetedSpending = (spending) => {
  if (!spending) return 0;
  
  // Sum all the monthly spending values
  const total = Object.values(spending).reduce((sum, value) => {
    // Make sure we're adding numbers, not strings
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return sum + numValue;
  }, 0);
  
  return Math.round(total * 100) / 100;
};

const Income = () => {
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile();
  const { data: paystubs, loading: paystubsLoading, error: paystubsError, refresh: refreshPaystubs } = usePaystubs();
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();

  const loading = profileLoading || paystubsLoading || transactionsLoading;
  const error = profileError || paystubsError || transactionsError;
  const refresh = () => {
    refreshProfile();
    refreshPaystubs();
    refreshTransactions();
  };

  if (loading) return <LoadingState message="Loading income data..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!profile || !paystubs || !transactions) return null;

  const monthlyNetIncome = getMonthlyNetIncome(paystubs);
  const budgetedSpending = getBudgetedSpending(profile.monthly_spending);
  
  // Calculate actual spending for the current/most recent month
  const actualSpending = calculateActualSpending(transactions, '2025-06'); // Using June as current month
  const netCashFlow = monthlyNetIncome - actualSpending.total;
  const budgetVariance = actualSpending.total - budgetedSpending;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Income & Spending Analysis</Text>
      
      {/* Income Summary */}
      <View style={styles.summaryGrid}>
        <View style={styles.incomeCard}>
          <Text style={styles.cardTitle}>Income Summary</Text>
          <View style={styles.incomeDetails}>
            <View style={styles.incomeRow}>
              <Text style={styles.incomeLabel}>Annual Salary:</Text>
              <Text style={styles.incomeValue}>${profile.income.toLocaleString()}</Text>
            </View>
            <View style={styles.incomeRow}>
              <Text style={styles.incomeLabel}>Pay Frequency:</Text>
              <Text style={styles.incomeValue}>Bi-weekly</Text>
            </View>
            <View style={styles.incomeRow}>
              <Text style={styles.incomeLabel}>Net per Paycheck:</Text>
              <Text style={styles.incomeValue}>~${Math.round(paystubs.reduce((sum, p) => sum + p.net, 0) / paystubs.length).toLocaleString()}</Text>
            </View>
            <View style={styles.incomeRow}>
              <Text style={styles.incomeLabel}>Gross per Paycheck:</Text>
              <Text style={styles.incomeValue}>~${Math.round(paystubs.reduce((sum, p) => sum + p.gross, 0) / paystubs.length).toLocaleString()}</Text>
            </View>
          </View>
        </View>
        
        {/* Monthly Cash Flow */}
        <View style={styles.cashFlowCard}>
          <Text style={styles.cardTitle}>Monthly Cash Flow</Text>
          <View style={styles.cashFlowDetails}>
            <View style={styles.cashFlowRow}>
              <Text style={styles.cashFlowLabel}>Monthly Net Income:</Text>
              <Text style={styles.cashFlowValue}>${monthlyNetIncome.toLocaleString()}</Text>
            </View>
            <View style={styles.cashFlowRow}>
              <Text style={styles.cashFlowLabel}>Actual Spending (June):</Text>
              <Text style={styles.cashFlowValue}>${actualSpending.total.toLocaleString()}</Text>
            </View>
            <View style={styles.cashFlowRow}>
              <Text style={styles.cashFlowLabel}>Net Cash Flow:</Text>
              <Text style={[
                styles.cashFlowValue,
                netCashFlow > 0 ? styles.positiveAmount : styles.negativeAmount
              ]}>
                {netCashFlow > 0 ? '+' : ''}${netCashFlow.toLocaleString()}
              </Text>
            </View>
          </View>
          <Text style={styles.cashFlowNote}>
            {netCashFlow > 0
              ? `Available for savings: $${netCashFlow.toLocaleString()}/month`
              : `Overspending by $${Math.abs(netCashFlow).toLocaleString()}/month`}
          </Text>
        </View>
      </View>

      {/* Budget vs Actual Comparison */}
      <View style={styles.comparisonCard}>
        <Text style={styles.cardTitle}>Budget vs Actual (June 2025)</Text>
        <View style={styles.comparisonGrid}>
          <View style={styles.budgetedCard}>
            <Text style={styles.comparisonLabel}>Budgeted</Text>
            <Text style={styles.budgetedAmount}>${budgetedSpending.toLocaleString()}</Text>
          </View>
          <View style={styles.actualCard}>
            <Text style={styles.comparisonLabel}>Actual</Text>
            <Text style={styles.actualAmount}>${actualSpending.total.toLocaleString()}</Text>
          </View>
          <View style={styles.varianceCard}>
            <Text style={styles.comparisonLabel}>Variance</Text>
            <Text style={[
              styles.varianceAmount,
              budgetVariance > 0 ? styles.negativeVariance : styles.positiveVariance
            ]}>
              {budgetVariance > 0 ? '+' : ''}${budgetVariance.toLocaleString()}
            </Text>
          </View>
        </View>
        <Text style={styles.varianceNote}>
          {budgetVariance > 0 
            ? `You spent $${budgetVariance.toLocaleString()} more than budgeted this month.`
            : budgetVariance < 0 
            ? `You spent $${Math.abs(budgetVariance).toLocaleString()} less than budgeted this month. Great job!`
            : 'You spent exactly your budgeted amount this month.'}
        </Text>
      </View>

      {/* Spending Breakdown */}
      <View style={styles.breakdownCard}>
        <Text style={styles.cardTitle}>Actual Spending Breakdown (June)</Text>
        <View style={styles.breakdownGrid}>
          {Object.entries(actualSpending.byCategory)
            .sort(([,a], [,b]) => b - a)
            .map(([category, amount]) => (
              <View key={category} style={styles.breakdownItem}>
                <Text style={styles.categoryName}>{category}</Text>
                <Text style={styles.categoryAmount}>${amount.toLocaleString()}</Text>
              </View>
            ))}
        </View>
      </View>

      {/* Recent Paystubs */}
      <View style={styles.paystubsCard}>
        <Text style={styles.cardTitle}>Recent Paystubs</Text>
        <View style={styles.tableContainer}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.dateColumn]}>Date</Text>
            <Text style={[styles.tableHeaderText, styles.employerColumn]}>Employer</Text>
            <Text style={[styles.tableHeaderText, styles.amountColumn]}>Gross</Text>
            <Text style={[styles.tableHeaderText, styles.amountColumn]}>Deductions</Text>
            <Text style={[styles.tableHeaderText, styles.amountColumn]}>Net</Text>
          </View>
          
          {/* Table Rows */}
          {paystubs.map((stub, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={[styles.tableCellText, styles.dateColumn]}>{stub.date}</Text>
              <Text style={[styles.tableCellText, styles.employerColumn]}>{stub.employer}</Text>
              <Text style={[styles.tableCellText, styles.amountColumn]}>${stub.gross.toLocaleString()}</Text>
              <Text style={[styles.tableCellText, styles.amountColumn]}>${stub.deductions.toLocaleString()}</Text>
              <Text style={[styles.tableCellText, styles.amountColumn, styles.netAmount]}>${stub.net.toLocaleString()}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1024, // max-w-4xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
  },
  
  // Summary Grid
  summaryGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 32,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  incomeCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cashFlowCard: {
    flex: 1,
    backgroundColor: '#F0FDF4',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 16,
  },
  incomeDetails: {
    gap: 4,
  },
  incomeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  incomeLabel: {
    fontSize: 14,
    color: '#374151',
  },
  incomeValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  cashFlowDetails: {
    gap: 4,
    marginBottom: 16,
  },
  cashFlowRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  cashFlowLabel: {
    fontSize: 14,
    color: '#374151',
  },
  cashFlowValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  positiveAmount: {
    color: '#059669',
  },
  negativeAmount: {
    color: '#DC2626',
  },
  cashFlowNote: {
    fontSize: 14,
    color: '#047857',
  },

  // Comparison Card
  comparisonCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  comparisonGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  budgetedCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#EFF6FF',
    borderRadius: 12,
  },
  actualCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
  },
  varianceCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#FFFBEB',
    borderRadius: 12,
  },
  comparisonLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  budgetedAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  actualAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#374151',
  },
  varianceAmount: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  positiveVariance: {
    color: '#059669',
  },
  negativeVariance: {
    color: '#DC2626',
  },
  varianceNote: {
    fontSize: 14,
    color: '#6B7280',
  },

  // Breakdown Card
  breakdownCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  breakdownGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    // Single column on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  breakdownItem: {
    flex: 1,
    minWidth: '45%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    // Full width on mobile
    '@media (max-width: 768px)': {
      minWidth: '100%',
    },
  },
  categoryName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    textTransform: 'capitalize',
  },
  categoryAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },

  // Paystubs Table
  paystubsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'left',
  },
  tableCellText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'left',
  },
  dateColumn: {
    flex: 2,
    paddingHorizontal: 8,
  },
  employerColumn: {
    flex: 3,
    paddingHorizontal: 8,
  },
  amountColumn: {
    flex: 2,
    paddingHorizontal: 8,
    textAlign: 'right',
  },
  netAmount: {
    fontWeight: '600',
    color: '#111827',
  },
});

export default Income; 